/**
 * Editorial explainer copy for each PGA.com coach accreditation card.
 * Keyed by the same `id` emitted by `buildCoachAccreditations`
 * (`src/lib/pga-coach/coach-credentials.js`), so a
 * card's modal content is looked up via the same identifier the card
 * itself renders with. Keep `id` values aligned across the two files.
 *
 * Each entry shapes a single modal:
 * - `title`     — centered Source Serif headline (`h3`, same treatment
 *                 as `/workingolf/career-services`) above the crest row
 * - `crests`    — brand crests below the title; usually 1-3 marks that
 *                 visually anchor the credential family. Each entry is
 *                 `{ src, alt }`. May be omitted to render a title-only
 *                 header
 * - `body`      — array of paragraph strings rendered as `<p>` blocks.
 *                 The first paragraph is bold; later ones stay regular
 *                 weight
 * - `teaser`    — optional condensed copy (≤2 sentences) kept as a
 *                 short form of `body`. The flagged-profile bio
 *                 membership banner no longer uses this — it names
 *                 the coach and their classification instead. Omit on
 *                 credentials that do not get a bio banner (ADM, HOPE)
 * - `ctaPrompt` — optional bold, centered line immediately above the
 *                 CTA (e.g. the membership closing question)
 * - `cta`       — optional pill-button CTA. With `href`, it navigates
 *                 to a follow-up destination (e.g. PGA of America's
 *                 membership funnel); absolute `https://` URLs open in
 *                 a new browser tab. Without `href`, it is a gray
 *                 dismiss control that closes the modal. Omit for
 *                 credentials with no footer action.
 *
 * Accreditations without an entry here render as static text in
 * `CoachAccreditationsRow` — no button, no dialog — so unsupplied
 * editorial content never produces a dead click target.
 */
const ACCREDITATION_EXPLAINERS = {
  'pga-member': {
    title: 'PGA of America Membership',
    crests: [
      // Navy circular Member and Master Professional marks (no gold ring).
      // The bannerless org wordmark is not useful in this explainer.
      { src: '/images/pga-member-logo.svg', alt: 'PGA of America Member' },
      {
        src: '/images/pga-master-logo.svg',
        alt: 'PGA of America Master Professional',
      },
    ],
    body: [
      "The PGA Member designation is the most respected in the golf industry. It's your sign that you'll receive instruction and guidance from the very best in the game.",
      'When you hire a PGA of America Member, you get the benefits of their rigorous, multi-year accreditation process, continuous professional development through seminars and workshops, and their active Teaching & Coaching community.',
      "PGA Members are equipped with unparalleled expertise in athlete and skill development — and they're ready to help you improve your game.",
    ],
    teaser: "The PGA Member designation is the most respected in the golf industry. It's your sign that you'll receive instruction and guidance from the very best in the game.",
    ctaPrompt: 'Are you interested in turning your passion into a profession?',
    cta: { label: 'Explore Membership Options', href: '/workingolf' },
  },
  'pga-associate': {
    title: 'PGA Associate',
    body: [
      'PGA Associates are working professionals enrolled in the PGA of America’s multi-year education program on their path to full PGA Membership.',
      'They earn an income in the golf industry while completing the same rigorous curriculum every Member completed — coaching theory, business of golf, rules, club fitting, and more — under the mentorship of an established PGA Professional.',
      'Hiring a PGA Associate means you’re working with someone who is actively investing in their craft and accountable to the PGA of America’s standards.',
    ],
    teaser: 'PGA Associates are working professionals enrolled in the PGA of America’s multi-year education program on their path to full PGA Membership. Hiring a PGA Associate means you’re working with someone who is actively investing in their craft and accountable to the PGA of America’s standards.',
    cta: { label: 'Close' },
  },
  'pga-student': {
    title: 'PGA of America Student',
    body: [
      'PGA Students are enrolled in a PGA Golf Management University Program — a four-and-a-half-year accredited curriculum that combines a bachelor’s degree with the PGA of America’s Professional Golf Management coursework and 16 months of paid internship experience.',
      'Hiring a PGA Student gives you access to a coach who is actively learning under the same education framework that produces every PGA Member, with one foot already in the industry.',
    ],
    teaser: 'PGA Students are enrolled in a PGA Golf Management University Program that combines a bachelor’s degree with PGA Professional Golf Management coursework. Hiring a PGA Student gives you access to a coach who is actively learning under the same education framework that produces every PGA Member.',
    cta: { label: 'PGA Golf Management University Program', href: '/workingolf' },
  },
  'master-professional': {
    title: 'PGA of America Master Professional',
    crests: [
      {
        src: '/images/pga-master-logo.svg',
        alt: 'PGA of America Master Professional',
      },
    ],
    body: [
      'PGA Master Professional is the highest education-and-experience designation granted by the PGA of America. It recognizes PGA Members who have completed a portfolio of advanced coursework, research, and service contributions in their area of expertise.',
      'A Master Professional has demonstrated mastery in a specific career path — coaching, general management, club fitting, executive leadership, ownership, or another PGA-recognized specialty — beyond the requirements of regular PGA Membership.',
      'Working with a PGA Master Professional means working with a coach whose peers and the PGA of America have formally recognized as a leader in the profession.',
    ],
    teaser: 'PGA Master Professional is the highest education-and-experience designation granted by the PGA of America. Working with a PGA Master Professional means working with a coach whose peers and the PGA of America have formally recognized as a leader in the profession.',
    cta: { label: 'Close' },
  },
  'adm-certified': {
    title: 'American Development Model Certified',
    crests: [
      {
        src: '/images/accreditations/adm-certified.png',
        alt: 'American Development Model Certified',
      },
    ],
    body: [
      'The American Development Model (ADM) is a long-term athlete development framework developed in partnership with the U.S. Olympic & Paralympic Committee and adopted across U.S. sport.',
      'PGA Coaches who complete the ADM certification are trained to coach golfers — particularly juniors — in age- and stage-appropriate ways that build foundational athletic skills, sustain enjoyment, and support long-term development in the game.',
      'When you hire an ADM-certified PGA Coach, you’re working with someone whose approach to teaching is grounded in the same principles that guide elite athlete development across U.S. sport.',
    ],
    cta: { label: 'Learn about ADM', href: '/adm' },
  },
  'pga-hope': {
    title: 'PGA HOPE Certified',
    crests: [
      {
        src: '/images/accreditations/pga-hope.png',
        alt: 'PGA HOPE Certified',
      },
    ],
    body: [
      'PGA HOPE (Helping Our Patriots Everywhere) is the flagship military program of PGA REACH, the charitable foundation of the PGA of America.',
      'PGA HOPE Certified Coaches have completed specialized training to introduce the game of golf to military Veterans with disabilities, helping them improve their physical, mental, social, and emotional well-being.',
      'A PGA HOPE Certified Coach hosts free, multi-week clinics for Veterans in their community and is trained to adapt the game to participants with a wide range of abilities and service-related conditions.',
    ],
    cta: {
      label: 'Learn about PGA HOPE',
      href: 'https://www.pgareach.org/pgahope',
    },
  },
}

/**
 * Returns the explainer entry for a given accreditation id, or `null`
 * when no editorial content exists. Callers should treat `null` as
 * "no modal" — render the card as a static surface and skip click
 * wiring entirely.
 */
export function getAccreditationExplainer(id) {
  if (!id) return null
  return ACCREDITATION_EXPLAINERS[id] || null
}

/**
 * Condensed (≤2 sentence) explainer teaser for a membership
 * classification id, or `null` when that id has no teaser.
 * The bio membership banner uses personalized coach copy instead.
 */
export function getAccreditationBannerTeaser(id) {
  return getAccreditationExplainer(id)?.teaser || null
}

export default ACCREDITATION_EXPLAINERS
